import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useMemo } from 'react';
import Modal from '../ui/modal';
import { drillWidget } from './network';
const PAGE_SIZE = 25;
const DrillThrough = ({ isOpen, onClose, widgetId, widgetName, drillFilters }) => {
    var _a, _b;
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [sort, setSort] = useState(null);
    // Reset state when modal opens or filters change
    useEffect(() => {
        if (isOpen) {
            setPage(1);
            setSort(null);
            setResult(null);
            setError(null);
        }
    }, [isOpen, widgetId, JSON.stringify(drillFilters)]);
    // Fetch data when page changes or modal opens
    useEffect(() => {
        if (!isOpen)
            return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        drillWidget(widgetId, drillFilters, page, PAGE_SIZE)
            .then(data => {
            if (!cancelled)
                setResult(data);
        })
            .catch(err => {
            if (!cancelled)
                setError((err === null || err === void 0 ? void 0 : err.message) || 'Failed to load drill-through data');
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => { cancelled = true; };
    }, [isOpen, widgetId, page, JSON.stringify(drillFilters)]);
    const columns = useMemo(() => {
        if (!result || result.items.length === 0)
            return [];
        return Object.keys(result.items[0]).filter(k => k !== '_id');
    }, [result]);
    const sortedItems = useMemo(() => {
        if (!result)
            return [];
        const items = [...result.items];
        if (!sort)
            return items;
        const { col, dir } = sort;
        items.sort((a, b) => {
            const aVal = a[col];
            const bVal = b[col];
            if (aVal == null && bVal == null)
                return 0;
            if (aVal == null)
                return dir === 'asc' ? -1 : 1;
            if (bVal == null)
                return dir === 'asc' ? 1 : -1;
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return dir === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const cmp = String(aVal).localeCompare(String(bVal));
            return dir === 'asc' ? cmp : -cmp;
        });
        return items;
    }, [result, sort]);
    const toggleSort = (col) => {
        setSort(prev => {
            if ((prev === null || prev === void 0 ? void 0 : prev.col) === col) {
                return prev.dir === 'asc' ? { col, dir: 'desc' } : null;
            }
            return { col, dir: 'asc' };
        });
    };
    const sortIcon = (col) => {
        if ((sort === null || sort === void 0 ? void 0 : sort.col) !== col)
            return 'bi-chevron-expand';
        return sort.dir === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
    };
    const totalPages = (_a = result === null || result === void 0 ? void 0 : result.totalPages) !== null && _a !== void 0 ? _a : 0;
    const totalCount = (_b = result === null || result === void 0 ? void 0 : result.total) !== null && _b !== void 0 ? _b : 0;
    return (_jsxs(Modal, { isOpen: isOpen, title: `Drill: ${widgetName}`, size: "xl", onClose: onClose, children: [_jsx("div", { className: "mb-2 d-flex flex-wrap gap-1", children: drillFilters.map((f, i) => {
                    var _a, _b;
                    return (_jsxs("span", { className: "badge bg-secondary-subtle text-dark border", children: [f.column, ": ", (_b = (_a = f.values) === null || _a === void 0 ? void 0 : _a.join(', ')) !== null && _b !== void 0 ? _b : ''] }, i));
                }) }), loading && (_jsxs("div", { className: "d-flex align-items-center justify-content-center py-5", children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2" }), " Loading..."] })), !loading && error && (_jsx("div", { className: "text-danger small text-center py-3", children: error })), !loading && !error && result && result.items.length === 0 && (_jsx("div", { className: "text-muted small text-center py-3", children: "No data" })), !loading && !error && result && result.items.length > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { style: { overflow: 'auto', maxHeight: '60vh' }, children: _jsxs("table", { className: "table table-sm table-hover table-bordered mb-0", children: [_jsx("thead", { className: "table-light", children: _jsx("tr", { children: columns.map(col => (_jsxs("th", { className: "small text-nowrap", style: { cursor: 'pointer', userSelect: 'none' }, onClick: () => toggleSort(col), children: [col, _jsx("i", { className: `bi ${sortIcon(col)} ms-1`, style: { fontSize: '0.65rem' } })] }, col))) }) }), _jsx("tbody", { children: sortedItems.map((row, i) => (_jsx("tr", { children: columns.map(col => (_jsx("td", { className: "small", children: row[col] == null ? '' : String(row[col]) }, col))) }, i))) })] }) }), _jsxs("div", { className: "d-flex justify-content-between align-items-center mt-2", children: [_jsxs("span", { className: "text-muted small", children: [totalCount.toLocaleString(), " total row", totalCount !== 1 ? 's' : ''] }), _jsxs("div", { className: "d-flex align-items-center gap-2", children: [_jsx("button", { className: "btn btn-sm btn-outline-secondary", disabled: page <= 1, onClick: () => setPage(p => Math.max(1, p - 1)), children: "Previous" }), _jsxs("span", { className: "small text-muted", children: ["Page ", page, " of ", totalPages] }), _jsx("button", { className: "btn btn-sm btn-outline-secondary", disabled: page >= totalPages, onClick: () => setPage(p => Math.min(totalPages, p + 1)), children: "Next" })] })] })] }))] }));
};
export default DrillThrough;
